import { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { usePatientProfileDetails } from '~/api/query/patientProfile';
import { USER_ROLES } from '~/features/auth/roles';
import { useModal } from '~/features/modals/useModal';
import {
  PATIENT_PROFILE_TABS,
  type PatientProfileTabsValue,
} from '~/features/patientAdministration/constants';
import { PatientProfileScreen } from '~/features/patientAdministration/patientProfile';
import { Route as PatientListRoute } from '~/routes/outpatient/patients.index';
import type { PatientProfileSearchParams } from '~/types/PatientRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const testID = 'patient-profile-route';

export const Route = createFileRoute('/outpatient/patients/$patientId')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): PatientProfileSearchParams<PatientProfileTabsValue> => {
    return {
      tab:
        search.tab !== undefined &&
        Object.values(PATIENT_PROFILE_TABS).includes(
          search.tab as PatientProfileTabsValue,
        )
          ? (search.tab as PatientProfileTabsValue)
          : PATIENT_PROFILE_TABS.overview,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.HcpUser],
    });
  },
});

function RouteComponent() {
  const { tab = PATIENT_PROFILE_TABS.overview } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { patientId } = Route.useParams();
  const { openModal } = useModal();
  const { data } = usePatientProfileDetails(patientId);

  const handleTabChange = useCallback(
    (newTab: PatientProfileTabsValue) => {
      void navigate({
        search: () => ({ tab: newTab }),
      });
    },
    [navigate],
  );

  const handlePatientProfileEdit = useCallback(() => {
    if (!data) {
      console.error('No patient data found');

      return;
    }

    openModal('PatientEditProfileForm', {
      patient: data,
      testID: buildTestId({
        testID,
        suffix: ['edit', 'default', 'alert', 'modal'],
      }),
      onCloseRedirectPath: Route.fullPath,
    });
  }, [data, openModal]);

  const handlePatientSettingsEdit = useCallback(() => {
    if (!data?.alertsSettings) {
      console.error('No alert settings found');

      return;
    }
    openModal('PatientEditAlertForm', {
      patientId,
      alertSettings: {
        ...data.alertsSettings,
      },
      testID: buildTestId({
        testID,
        suffix: ['edit', 'default', 'alert', 'modal'],
      }),
      onCloseRedirectPath: Route.fullPath,
    });
  }, [data, openModal, patientId]);

  const handleArchivePatient = useCallback(() => {
    openModal('ArchivePatient', {
      testID: buildTestId({
        testID,
        suffix: ['archive', 'patient', 'modal'],
      }),
      onArchive: async () => {
        // TODO: Call the archive patient API
        await Promise.resolve();
      },
    });
  }, [openModal, testID]);

  const handleBreadCrumbPress = useCallback(() => {
    void navigate({ to: PatientListRoute.fullPath });
  }, [navigate]);

  return (
    <PatientProfileScreen
      patientId={patientId}
      tab={tab}
      handleTabChange={handleTabChange}
      handlePatientProfileEdit={handlePatientProfileEdit}
      handlePatientSettingsEdit={handlePatientSettingsEdit}
      handleArchivePatient={handleArchivePatient}
      handleBreadCrumbPress={handleBreadCrumbPress}
    />
  );
}
